import { EdlProficiencyPickerOptions } from "@edl-development/edl-common";
import { DomainFilterType } from "src/app/model/enum/observations/domain-filter-type.enum";
import { DomainLookup } from "src/app/model/interfaces/DomainLookup.interface";
import { SubdomainLookup } from "src/app/model/interfaces/SubdomainLookup.interface";
import { ResourceData } from "src/app/services/resource/resource.service";
import { Domain } from "../../../model/enum/observations/domain.enum";
import { Subdomain } from "../../../model/enum/observations/subdomain.enum";
import { RubricResponse } from "../../../model/enum/rubric/rubric-response.enum";

export class ObservationLookUpTypes {
    private static readonly Domains: DomainLookup[] = [
        {
            Label: 'Planning',
            Value: Domain.PLANNING,
            Subdomains: [
                { Label: '1a. Content Knowledge', LongLabel: 'Knowledge of Content and Pedagogy', Value: Subdomain.CONTENT_KNOWLEDGE, Key: "1a" },
                { Label: '1b. Student Knowledge', LongLabel: 'Demonstrating Knowledge of Students', Value: Subdomain.STUDENT_KNOWLEDGE, Key: "1b" },
                { Label: '1c. Setting Outcomes', LongLabel: 'Setting Instructional Outcomes', Value: Subdomain.SETTING_OUTCOMES, Key: "1c" },
                { Label: '1d. Resource Knowledge', LongLabel: 'Demonstrating Knowledge of Resources', Value: Subdomain.RESOURCE_KNOWLEDGE, Key: "1d" },
                { Label: '1e. Coherent Instruction', LongLabel: 'Designing Coherent Instruction', Value: Subdomain.COHERENT_INSTRUCTION, Key: "1e" },
                { Label: '1f. Student Assessment', LongLabel: 'Designing Student Assessment', Value: Subdomain.STUDENT_ASSESSMENT, Key: "1f" }
            ],
            Key: 'Domain1_Title.Text'
        },
        {
            Label: 'Classroom Environment',
            Value: Domain.CLASSROOM_ENVIRONMENT,
            Subdomains: [
                { Label: '2a. Environment of Respect', LongLabel: 'Creating an Environment of Respect and Rapport', Value: Subdomain.ENVIRONMENT_OF_RESPECT, Key: "2a" },
                { Label: '2b. Culture for Learning', LongLabel: 'Establishing a Culture for Learning', Value: Subdomain.CULTURE_FOR_LEARNING, Key: "2b" },
                { Label: '2c. Classroom Procedures', LongLabel: 'Managing Classroom Procedures', Value: Subdomain.CLASSROOM_PROCEDURES, Key: "2c" },
                { Label: '2d. Student Behavior', LongLabel: 'Managing Student Behavior Expectations', Value: Subdomain.STUDENT_BEHAVIOUR, Key: "2d" },
                { Label: '2e. Physical Space', LongLabel: 'Organizing Physical and Digital Space', Value: Subdomain.PHYCICAL_SPACE, Key: "2e" }
            ],
            Key: 'Domain2_Title.Text'
        },
        {
            Label: 'Instruction',
            Value: Domain.INSTRUCTION,
            Subdomains: [
                { Label: '3a. Communicating with Students', LongLabel: 'Communicating with Students', Value: Subdomain.COMMUNICATING_WITH_STUDENTS, Key: "3a" },
                { Label: '3b. Questioning Techniques', LongLabel: 'Questioning and Discussion Techniques', Value: Subdomain.QUESTIONING_TECHNIQUES, Key: "3b" },
                { Label: '3c. Engaging Students', LongLabel: 'Engaging Students in Learning Activities and Assignments', Value: Subdomain.ENGAGING_STUDENTS, Key: "3c" },
                { Label: '3d. Using Assessment', LongLabel: 'Using Assessment in Instruction', Value: Subdomain.USING_ASSESSMENT, Key: "3d" },
                { Label: '3e. Flexibility', LongLabel: 'Demonstrating Flexibility and Responsiveness', Value: Subdomain.FLEXIBILITY, Key: "3e" }
            ],
            Key: 'Domain3_Title.Text'
        },
        {
            Label: 'Professionable Responsibilities',
            Value: Domain.PROFESSIONAL_RESPONSIBILITIES,
            Subdomains: [
                { Label: '4a. Reflecting', LongLabel: 'Reflecting on Teaching', Value: Subdomain.REFLECTING, Key: "4a" },
                { Label: '4b. Accurate Records', LongLabel: 'Maintaining Accurate Records', Value: Subdomain.ACCURATE_RECORDS, Key: "4b" },
                { Label: '4c. Communicating', LongLabel: 'Communicating with Families', Value: Subdomain.COMMUNICATING, Key: "4c" },
                { Label: '4d. Professional Community', LongLabel: 'Participating in a Professional Community', Value: Subdomain.PROFESSIONAL_COMMUNITY, Key: "4d" },
                { Label: '4e. Growing', LongLabel: 'Growing and Developing Professionally', Value: Subdomain.GROWING, Key: "4e" },
                { Label: '4f. Professionalism', LongLabel: 'Showing Professionalism', Value: Subdomain.PROFESSIONALISM, Key: "4f" }
            ],
            Key: 'Domain4_Title.Text'
        }
    ];
    private static readonly RubricResponses: { [key: number]: { DefaultTitle: string, HexColor: string, TitleKeyIndex?: number } } = {
        [RubricResponse.INAPPLICABLE]: {
            DefaultTitle: 'Not Applicable',
            HexColor: '#000000',
        },
        [RubricResponse.UNSELECTED]: {
            DefaultTitle: 'Unselected',
            HexColor: '#dde2e4',
        },
        [RubricResponse.FAILING]: {
            DefaultTitle: 'Failing',
            HexColor: '#b34443',
            TitleKeyIndex: 0
        },
        [RubricResponse.NEEDS_IMPROVEMENT]: {
            DefaultTitle: 'Needs Improvement',
            HexColor: '#fcbd24',
            TitleKeyIndex: 1
        },
        [RubricResponse.PROFICIENT]: {
            DefaultTitle: 'Proficient',
            HexColor: '#48b96e',
            TitleKeyIndex: 2
        },
        [RubricResponse.EXCEEDS_EXPECTATION]: {
            DefaultTitle: 'Distinguished',
            HexColor: '#006590',
            TitleKeyIndex: 3
        }
    };

    public static generateRubricResponses = (resourceFile: ResourceData = {}, resourceFileKey: string = ''): EdlProficiencyPickerOptions[] =>
        [RubricResponse.FAILING, RubricResponse.NEEDS_IMPROVEMENT, RubricResponse.PROFICIENT, RubricResponse.EXCEEDS_EXPECTATION]
            .map((option) => this.generateRubricResponse(option, resourceFile, resourceFileKey));

    public static generateRubricResponses_All = (resourceFile: ResourceData = {}, resourceFileKey: string = ''): { [key:number]: EdlProficiencyPickerOptions} => {
        const keys = Object.values(RubricResponse).filter(v => typeof v === 'number').map(v => v as RubricResponse);
        return keys.reduce((acc, option) => {
            acc[option] = this.generateRubricResponse(option, resourceFile, resourceFileKey);
            return acc;
        }, {});
    }

    public static generateRubricResponse(option: RubricResponse, resourceFile: ResourceData = {}, resourceFileKey: string = ''): EdlProficiencyPickerOptions {
        const rubricResponse = this.RubricResponses[option];

        const descriptionKey = resourceFileKey + "_" + (rubricResponse.DefaultTitle.replace(/\s/g, "")) + ".Text";
        const optionTitleKey = rubricResponse.TitleKeyIndex !== undefined ? "BoxHeader" + (rubricResponse.TitleKeyIndex + 1) + ".Text" : undefined;

        return {
            Title: (optionTitleKey ? resourceFile[optionTitleKey] : undefined) ?? rubricResponse.DefaultTitle,
            Description: resourceFile[descriptionKey] ?? '',
            Value: option,
            HexColor: rubricResponse.HexColor
        }
    }

    public static generateRubricResponsePrint(option: RubricResponse, resourceFile: ResourceData = {}, resourceFileKey: string = ''): EdlProficiencyPickerOptions {
        const baseResponse = this.generateRubricResponse(option, resourceFile, resourceFileKey);
        //if the baseResponse has an Unselected title, replace it with No Rating
        if (baseResponse.Title === 'Unselected') {
            baseResponse.Title = 'No Rating';
        }
        return baseResponse;
    }

    public static generateDomains(resourceFile: ResourceData = {}): DomainLookup[] {
        return this.updateTextBasedOnResourceFile(resourceFile, this.Domains);
    }

    public static generateAllComponents(resourceFile: ResourceData = {}): SubdomainLookup[] {
        const domains = this.generateDomains(resourceFile);
        return domains.flatMap(d => d.Subdomains);
    }

    private static updateTextBasedOnResourceFile(resourceFile: ResourceData = {}, rubricDomains: DomainLookup[] = []): DomainLookup[] {
        return rubricDomains.map(domain => {
            return {
                Label: resourceFile[domain.Key] ?? domain.Label,
                Value: domain.Value,
                Subdomains: this.updateResourceFileText(resourceFile, domain),
                Key: domain.Key
            }
        });
    }

    private static updateResourceFileText(resourceFile: ResourceData = {}, rubricDomain: DomainLookup): SubdomainLookup[] {

        if (resourceFile === null || resourceFile === (<any>{}) || Object.keys(resourceFile).length === 0) {
            return rubricDomain.Subdomains.map(component => {
                const resourceFileKey = "btn" + component.Key + ".Text"
                const longLabelKey = component.Key + '_Title.Text';
                return {
                    Label: resourceFile[resourceFileKey] ?? component.Label,
                    LongLabel: resourceFile[longLabelKey] ?? component.LongLabel,
                    Value: component.Value,
                    Key: component.Key
                }
            }).filter(c => c.Label);
        } else {
            const domainKey = rubricDomain.Key.replace('Domain', '').replace('_Title.Text', '');
            const keys = Object.keys(resourceFile).filter(k => k.startsWith('btn')).map(k => k.replace('btn', '').replace('.Text', ''));
            const keysFiltered = keys.filter(k => k.charAt(0) == domainKey.charAt(0));

            return keysFiltered.map(key => {
                const resourceFileKey = "btn" + key + ".Text"
                const longLabelKey = key + '_Title.Text';
                return {
                    Label: resourceFile[resourceFileKey],// ?? component?.Label,
                    LongLabel: resourceFile[longLabelKey],// ?? component?.LongLabel,
                    Value: key,// ?? component?.Value,
                    Key: key
                } as SubdomainLookup;
            }) as SubdomainLookup[];
        }


    }

    /* Domain Filtering */

    public static filterDomains(domains: DomainLookup[] = [], filterType?: DomainFilterType, currentSubdomain?: Subdomain, focusAreas?: string[]): DomainLookup[] {
        if (filterType === null || filterType === undefined || currentSubdomain === null || currentSubdomain === undefined) {
            return domains
        }

        return domains
            .map(r => ({
                Label: r.Label,
                Value: r.Value,
                Subdomains: this.filterSubdomains(r, filterType, currentSubdomain, focusAreas),
                Key: r.Key
            })).filter(r => r.Subdomains.length > 0);
    }

    private static filterSubdomains(domain: DomainLookup, filterType: DomainFilterType, currentSubdomain: Subdomain, focusAreas?: string[]): SubdomainLookup[] {
        switch (filterType) {
            case DomainFilterType.COMPONENT:
                return domain.Subdomains?.filter(r => r.Value == currentSubdomain);
            case DomainFilterType.DOMAIN:
                return domain.Subdomains?.some(r => r.Value == currentSubdomain) ? domain.Subdomains : [];
            case DomainFilterType.FOCUS_AREAS:
                return domain.Subdomains?.filter(r => focusAreas && focusAreas.includes(r.Value));
            default:
                return domain.Subdomains;
        }
    }

}

